import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable, of } from 'rxjs';
import {
  ClinicalRequestDto, ClinicalRequestListDto, ClinicalRequestOpenCountDto,
} from '../../dtos/clinical-request.dto';

@Injectable()
export class ClinicalRequestsRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  // Absent keys are left out of the params object entirely rather than sent empty, matching
  // HttpClient's own "no key => no query-string entry" behaviour (same shape as
  // `AppointmentRequestsRemoteDatasource.list`).
  public list(status?: string, departmentId?: number): Observable<ClinicalRequestListDto> {
    const params: Record<string, string> = {};
    if (status) params['status'] = status;
    if (departmentId !== undefined) params['departmentId'] = String(departmentId);

    return this.http.get<ClinicalRequestListDto>(`${this.apiUrl}${API_ENDPOINTS.clinicalRequests.list}`, { params });
  }

  // Resolves an explicit set of ids regardless of status, for the chat cards whose request has
  // already been resolved and therefore left the pending queue `list()` serves. An empty set is
  // short-circuited rather than sent: `ids` absent would make the backend reject the request, and
  // there is nothing to ask for anyway. Angular's object-literal params emit a string array as one
  // repeated key (`?ids=a&ids=b`), which is the shape the `[FromQuery] Guid[] ids` binder expects.
  public listByIds(ids: readonly string[]): Observable<ClinicalRequestListDto> {
    if (ids.length === 0) return of<ClinicalRequestListDto>({ Items: [], Count: 0 });

    return this.http.get<ClinicalRequestListDto>(
      `${this.apiUrl}${API_ENDPOINTS.clinicalRequests.byIds}`, { params: { ids: [...ids] } },
    );
  }

  public openCount(departmentId?: number): Observable<ClinicalRequestOpenCountDto> {
    const params: Record<string, string> = {};
    if (departmentId !== undefined) params['departmentId'] = String(departmentId);

    return this.http.get<ClinicalRequestOpenCountDto>(
      `${this.apiUrl}${API_ENDPOINTS.clinicalRequests.openCount}`, { params },
    );
  }

  public updateStatus(id: string, payload: Record<string, unknown>): Observable<ClinicalRequestDto> {
    return this.http.patch<ClinicalRequestDto>(`${this.apiUrl}${API_ENDPOINTS.clinicalRequests.byId(id)}`, payload);
  }
}
